<template>
	<user-layout
		hasBack
		:backLink="`/patient/${patientId}/cases/${caseId}/edit`"
		hasBackClass="flex md:justify-between md:items-center md:flex-row flex-col">
		<template #lowerRight>
			<div class="flex md:flex-row flex-col items-center md:space-x-3 md:space-y-0 space-y-3">
				<a
					@click="
						router.push(
							`/patient/${patientId}/cases/${caseId}/edit`
						)
					"
					class="md:w-auto w-full md:mt-0 mt-6">
					<button-component btnWidth="quinary" customClass="quinary md:w-auto w-full">
						Cancel
					</button-component>
				</a>
				<button-component
					customClass="primary md:w-auto w-full md:mt-0 mt-6"
					form="form"
					@click="validateForm()"
				>
					<span>Save Changes</span>
				</button-component>
			</div>
		</template>
		<p class="mt-6 md:text-2xl text-lg font-medium text-neutral-600">Patient's Name:
			<span class="md:inline-block block font-semibold text-neutral-900">{{ patient }}</span>
		</p>
		<div class="w-full lg:p-6 p-4 mx-auto my-6 border border-gray-200 rounded">
			<div class="flex items-center justify-between">
				<section-title title="Informant Details" />
			</div>
			<form-section
				v-if="loaded"
				:form="form"
				:errors="errors"
				:genders="genders"
				:companionRelationships="companionRelationships" 
                @update:form="(value: any) => form = value"
                />
		</div>
		<success-modal
			:show="showSuccess"
			@confirm="redirectSuccess()"
			action-text="Close"
			title="Record Save!"
			:content="successMessage" />
	</user-layout>
 </template>
 <script setup lang="ts">
 import { ref,computed, watch } from "vue";
 import UserLayout from "@/layouts/UserLayout.vue";
 import SectionTitle from "@/components/partials/SectionTitle.vue";
 import ButtonComponent from "@/components/buttons/ButtonComponent.vue";
 import SuccessModal from "@/components/modals/SuccessModal.vue";
 import FormSection from "./FormSection.vue";
 import { useRouter, useRoute } from "vue-router";
 import { renderSelect, renderSelectV2 } from "@/interfaces/DropdownInterface";
 import CompanionService from "@/classes/Cases/CompanionService"; //TODO will remove
 import CompanionForm from "@/classes/Forms/Cases/CompanionForm"; //TODO will remove
 import { errorResponse } from "@/helpers/errorMessage";
 import pickBy from "lodash/pickBy";
 import { useRecordStatusChecker } from "@/store/recordStatusChecker";
 import { onIonViewWillEnter } from "@ionic/vue";
 
 
 /******************************* For Offline Imports************************************ */
 import { useNetworkStore } from "@/store/network";
 import CaseCompanionForm from "@/classes/Cases/CaseInformant/Offline/Formatters/CaseCompanionForm";
 import ErrorValidation from "@/views/interventions/Components/Classess/ErrorValidation";
 import Rules from "@/classes/Cases/CaseInformant/Offline/Validation/Rules";
 import CaseInformantService from "@/classes/Cases/CaseInformant/CaseInformantService";
 import { CompanionCreate } from "@/classes/Cases/CaseInformant/Offline/Interface/CompanionInterface";
 import useVuelidate from "@vuelidate/core";
 import CaseInformantOfflineProcessor from "@/classes/Cases/CaseInformant/Offline/Processor/OfflineProcessor";
 import OnlineSynching from "@/classes/Synching/OnlineSynching";
 const { createRules } = Rules;
 const networkStore = useNetworkStore();
 const isNetworkAvailable = computed(() => networkStore.isNetworkAvailable);
 /*************************************************************************************** */
 
 
 const router = useRouter();
 const route = useRoute();
 const showSuccess = ref<boolean>(false);
 const patientId = ref<number | null>(route.params.patient);
 const caseId = ref<number | null>(route.params.case);
 const companionId = ref<number | null>(route.params.companion);
 const genders = ref<renderSelectV2[]>([]);
 const companionRelationships = ref<renderSelect[]>([]);
 
 
 //This form used by both online and offline
 const form = ref<CompanionCreate>(CaseCompanionForm.updateForm({}));
 
 
 const errors = ref<object>({});
 const successMessage = ref<string>("Companions details has been updated!");
 const loaded = ref<boolean>(false);
 const patient = ref<string>('');
 const recordStatusChecker = useRecordStatusChecker();
 const onlineSync = new OnlineSynching();
 
 /**
 * Validate the form using Vuelidator
 */
 const validateForm = () =>{
	const vuelidate = useVuelidate(createRules({...form.value}), form.value)
	const result = vuelidate.value.$validate();
 
	result
		.then((res) => {
			if(res) {
				save()
			} else {
				errors.value = ErrorValidation.getErrors(vuelidate.value)
                errorResponse(errors.value);
			}
		})
 }
 
 
 const save = async (): Promise<any> => {
	errors.value = {};
 
	const response = await CaseInformantService.update(
		patientId.value,
		caseId.value,
		companionId.value,
		{ ...form.value },
		isNetworkAvailable.value
	);
    
    if (response?.response?.status === 404) {
        return;
    }
 
	if (response?.status === 200 || response?.data != undefined) {
        if (isNetworkAvailable.value) {
            await CaseInformantOfflineProcessor.syncToOffline(caseId.value, response.data.data.item);
        }
		setTimeout(() => {
			recordStatusChecker.isCreatedInformant = true;
			showSuccess.value = true;
			successMessage.value = response.data.message;
		}, 500);
	} else {
		const error = response;
		errors.value = errorResponse(error.response?.data?.errors);
	}
 };
 
 
 const redirectSuccess = async (): Promise<any> => {
	showSuccess.value = false;
	setTimeout(() => {
		router.push(`/patient/${patientId.value}/cases/${caseId.value}/edit`);
	}, 500);
 };
 
 
 const getData = async () => {  
	const response = await CaseInformantService.edit(
		patientId.value,
		caseId.value,
		companionId.value,
		pickBy({}),
		isNetworkAvailable.value
	);

    if (response?.response?.status === 404) {
        loaded.value = false;
        return;
    }
 
	if (response?.status === 200 || response?.data != undefined) {
		genders.value = response.data.genders;
		patient.value = response.data.patient;
		companionRelationships.value = response.data.companionRelationships;
		form.value = CaseCompanionForm.updateForm(response.data.item);
		
		if(!isNetworkAvailable.value){
			form.value = CaseCompanionForm.updateForm(response.data.item[0]); 
			genders.value = response.data.genders;
			patient.value = response.data.patient;
			companionRelationships.value = response.data.companionRelationships;
		}

        if (isNetworkAvailable.value) {
            await CaseInformantOfflineProcessor.syncToOffline(caseId.value, response.data.item)
        }
		
		loaded.value = true;
	} else {
		console.log(response);
	}
 };
 
 watch(
    () => isNetworkAvailable.value,
    async () => {
        loaded.value = false;
        await getData();
        await onlineSync.checkDrafts();
    }
)
 
 onIonViewWillEnter(async () => {
	recordStatusChecker.isCreatedInformant = false;
	await getData();   
    await onlineSync.checkDrafts();
 });
 </script>